// src/services/SearchService.ts
import { z } from 'zod';
import { challengeServiceClient } from '@/services/api';

// ----------------------------- Schemas & Types ------------------------------

const SuggestionSchema = z.object({
    type: z.enum(['tag', 'key']),
    term: z.string(),
    count: z.number().catch(0),
    sample_route: z.string().optional(),
    sample_thumb: z.string().optional(),
});

const SuggestResponseSchema = z.object({
    query: z.string(),
    suggestions: z.array(SuggestionSchema),
    note: z.string().optional(),
});
export type Suggestion = z.infer<typeof SuggestionSchema>;
export type SuggestResponse = z.infer<typeof SuggestResponseSchema>;

const TrendingTermSchema = z.object({
    type: z.enum(['tag', 'key']),
    term: z.string(),
    hot_score: z.number().catch(0),
});
const TrendingResponseSchema = z.object({
    window: z.string(),
    type: z.enum(['tag', 'key']),
    terms: z.array(TrendingTermSchema),
});
export type TrendingTerm = z.infer<typeof TrendingTermSchema>;
export type TrendingResponse = z.infer<typeof TrendingResponseSchema>;

const SearchItemSchema = z.object({
    item_id: z.string(),
    item_type: z.enum(['POST', 'RESPONSE', 'CHALLENGE']),
    thumb_url: z.string().nullable().optional(),
    route: z.string(),
    created_at: z.string(),
});
const ResultsResponseSchema = z.object({
    type: z.enum(['tag', 'key']),
    term: z.string(),
    total_count: z.number().catch(0),
    items: z.array(SearchItemSchema),
    last_evaluated_key: z.unknown().nullable().optional(),
});
export type TypeaheadItem = {
    kind: 'tag' | 'key';
    term: string;
    count?: number;
    sample_thumb?: string | null;
    sample_route?: string | null;
};

export type TypeaheadResponse = { items: TypeaheadItem[] };
export type SearchItem = z.infer<typeof SearchItemSchema>;
export type ResultsResponse = z.infer<typeof ResultsResponseSchema>;

// ----------------------------- Helper utils --------------------------------

const serializeCursor = (cursor?: any) => {
    if (!cursor) return undefined;
    if (typeof cursor === 'string') return cursor;
    try {
        return JSON.stringify(cursor);
    } catch {
        return undefined;
    }
};

// ------------------------------- Service -----------------------------------

class SearchService {
    /**
     * Get typeahead suggestions for a prefix.
     * @param q - the user query (prefix). must be length >= 2 or backend returns note:min_len
     * @param types - 'tag' | 'key' | 'both'
     * @param limit - number of suggestions (default 10)
     */
    static async suggest(
        q: string,
        types: 'tag' | 'key' | 'both' = 'both',
        limit = 10
    ): Promise<SuggestResponse> {
        const resp = await challengeServiceClient.get('/search/suggest', {
            params: { q, types, limit },
        });

        // Interceptors may return null on certain errors; normalize
        if (!resp || !resp.data) {
            return { query: q, suggestions: [] };
        }

        return SuggestResponseSchema.parse(resp.data);
    }

    /**
     * Get trending terms for the search bar (used when input is empty).
     * @param type - 'tag' | 'key'
     * @param limit - max terms (default 20)
     * @param window - '48H' by default (server supports this bucket)
     */
    static async trending(
        type: 'tag' | 'key' = 'tag',
        limit = 20,
        window = '48H'
    ): Promise<TrendingResponse> {
        const resp = await challengeServiceClient.get('/search/trending', {
            params: { type, limit, window },
        });

        if (!resp || !resp.data) {
            return { window, type, terms: [] };
        }

        return TrendingResponseSchema.parse(resp.data);
    }

    /**
     * Fetch items for a selected term.
     * @param type - 'tag' | 'key'
     * @param q - the exact term (e.g., 'bmx')
     * @param limit - items per page (default 25)
     * @param cursor - pass the `last_evaluated_key` from previous call (string or object)
     */
    static async results(
        type: 'tag' | 'key',
        q: string,
        limit = 25,
        cursor?: any
    ): Promise<ResultsResponse> {
        const resp = await challengeServiceClient.get('/search/results', {
            params: {
                type,
                q,
                limit,
                cursor: serializeCursor(cursor),
            },
        });

        if (!resp || !resp.data) {
            return { type, term: q, total_count: 0, items: [], last_evaluated_key: null };
        }

        return ResultsResponseSchema.parse(resp.data);
    }

    // src/services/SearchService.ts
    static async typeahead(q: string, limit = 12): Promise<TypeaheadResponse> {
        // normalize input and infer kind when user types '#...'
        const rawQ = (q ?? '').trim();
        const kind = rawQ.startsWith('#') ? 'tag' : undefined;
        const cleanQ = rawQ.replace(/^#/, '').toLowerCase();

        const resp = await challengeServiceClient.get('/search/suggest', {
            params: { q: cleanQ, limit, kind }, // pass kind so BE can prefer tag results
        });

        const raw = resp?.data ?? {};
        const arr = Array.isArray(raw.items)
            ? raw.items
            : Array.isArray(raw.suggestions)
              ? raw.suggestions
              : Array.isArray(raw.terms)
                ? raw.terms
                : [];

        const normalized: TypeaheadItem[] = arr
            .map((it: any) => ({
                kind:
                    it.kind === 'tag' || it.term_kind === 'tag' || it.type === 'tag'
                        ? 'tag'
                        : 'key',
                term: String(it.term ?? it.value ?? '').toLowerCase(),
                count:
                    typeof it.count === 'number'
                        ? it.count
                        : typeof it.total_count === 'number'
                          ? it.total_count
                          : undefined,
                sample_thumb: it.sample_thumb ?? it.thumb ?? null,
                sample_route: it.sample_route ?? it.route ?? null,
            }))
            .filter((it: TypeaheadItem) => it.term.length > 0);

        return { items: normalized };
    }
}

export default SearchService;
